import {
  MEETING_LIMITS,
  MEETING_TYPES,
  type MeetingDetail,
  meetingCreateSchema,
  type ReportPreview,
} from '@fruuit/shared'
import {
  Button,
  Callout,
  Checkbox,
  Dialog,
  DialogCancel,
  Input,
  Select,
  Textarea,
} from '@fruuit/ui'
import { type FormEvent, useEffect, useMemo, useState } from 'react'
import { errorMessage, isApiError } from '../../api/client'
import { useApiQuery } from '../../api/hooks'
import { useSession } from '../../auth/session-context'
import { browserZone, utcToZoned, zonedToUtc, zoneOptions } from '../calendar/time'

export interface MeetingFormDialogProps {
  open: boolean
  onClose: () => void
  /** Edit this draft; omit to schedule a new meeting. */
  meeting?: MeetingDetail
  onSubmit: (body: Record<string, unknown>) => Promise<unknown>
}

interface FormState {
  engagement_id: string
  title: string
  meeting_type: string
  starts: string
  ends: string
  timezone: string
  location: string
  agenda: string
  company_participants: string
  cutoff: string
  objective_ids: string[]
}

function initial(meeting: MeetingDetail | undefined): FormState {
  const zone = meeting?.timezone ?? browserZone()
  return {
    engagement_id: meeting?.engagement.id ?? '',
    title: meeting?.title ?? '',
    meeting_type: meeting?.meeting_type ?? MEETING_TYPES.values[0],
    starts: meeting ? utcToZoned(meeting.starts_at, zone) : '',
    ends: meeting ? utcToZoned(meeting.ends_at, zone) : '',
    timezone: zone,
    location: meeting?.location ?? 'Video conference',
    agenda: meeting?.agenda ?? '',
    company_participants: meeting?.company_participants ?? '',
    cutoff:
      meeting && meeting.participation_cutoff !== meeting.starts_at
        ? utcToZoned(meeting.participation_cutoff, zone)
        : '',
    objective_ids: meeting?.objectives.map((o) => o.id) ?? [],
  }
}

/**
 * Schedule or edit a draft meeting (CAL-021..026, GAP-UI-014): location and
 * related objectives included. Times are entered in a named zone and sent as
 * UTC. Limits come from the shared server schema (P-UI-008).
 */
export function MeetingFormDialog({ open, onClose, meeting, onSubmit }: MeetingFormDialogProps) {
  const me = useSession()
  const [form, setForm] = useState<FormState>(() => initial(meeting))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [failure, setFailure] = useState<string | null>(null)
  const [pending, setPending] = useState(false)

  useEffect(() => {
    if (open) {
      setForm(initial(meeting))
      setErrors({})
      setFailure(null)
    }
  }, [open, meeting])

  const engagementsQuery = useApiQuery<ReportPreview>(
    ['meetings', 'form', 'engagements'],
    '/reports/records.engagements/preview',
    { query: { view: 'staff', limit: 5000 }, enabled: open && !meeting },
  )
  const objectivesQuery = useApiQuery<ReportPreview>(
    ['meetings', 'form', 'objectives'],
    '/reports/records.objectives/preview',
    { query: { view: 'staff', limit: 5000 }, enabled: open },
  )

  const engagementOptions = useMemo(
    () =>
      (engagementsQuery.data?.rows ?? [])
        .filter((r) => r.assigned_manager_id === me.user.id && r.status !== 'closed')
        .map((r) => ({ value: String(r.id), label: `${r.company} · ${r.title}` })),
    [engagementsQuery.data, me.user.id],
  )
  const engagementId = meeting?.engagement.id ?? form.engagement_id
  const objectiveOptions = (objectivesQuery.data?.rows ?? []).filter(
    (r) => r.engagement_id === engagementId,
  )

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    const startsAt = zonedToUtc(form.starts, form.timezone)
    const endsAt = zonedToUtc(form.ends, form.timezone)
    const cutoff = form.cutoff ? zonedToUtc(form.cutoff, form.timezone) : null
    const body: Record<string, unknown> = {
      engagement_id: engagementId,
      title: form.title,
      meeting_type: form.meeting_type,
      starts_at: startsAt ?? '',
      ends_at: endsAt ?? '',
      timezone: form.timezone,
      location: form.location,
      agenda: form.agenda,
      ...(form.company_participants.trim()
        ? { company_participants: form.company_participants }
        : {}),
      ...(cutoff ? { participation_cutoff: cutoff } : {}),
      objective_ids: form.objective_ids,
    }
    const parsed = meetingCreateSchema.safeParse(body)
    const local: Record<string, string> = {}
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? 'form')
        if (!(key in local)) local[key] = issue.message
      }
    }
    if (startsAt && endsAt && endsAt <= startsAt)
      local.ends_at = 'The meeting must end after it starts.'
    if (Object.keys(local).length > 0) {
      setErrors(local)
      return
    }
    setErrors({})
    setFailure(null)
    setPending(true)
    try {
      if (meeting) {
        const { engagement_id: _ignored, ...rest } = body
        await onSubmit({
          ...rest,
          revision: meeting.revision,
          participation_cutoff: cutoff,
          company_participants: form.company_participants.trim() || meeting.company_participants,
        })
      } else {
        await onSubmit(body)
      }
      onClose()
    } catch (err) {
      if (isApiError(err) && Object.keys(err.fieldErrors).length > 0) setErrors(err.fieldErrors)
      setFailure(errorMessage(err))
    } finally {
      setPending(false)
    }
  }

  const zoneHint = `Times in ${form.timezone.replace(/_/g, ' ')}; stored in UTC.`

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={meeting ? 'Edit draft meeting' : 'Schedule a meeting'}
      description="Drafts are visible to the case team only. Publishing announces the agenda to subscribed investors. No invitations are sent."
      size="lg"
      footer={
        <>
          <DialogCancel />
          <Button type="submit" form="meeting-form" loading={pending}>
            {meeting ? 'Save draft' : 'Create draft'}
          </Button>
        </>
      }
    >
      <form id="meeting-form" onSubmit={submit} noValidate className="grid gap-4 sm:grid-cols-2">
        {failure ? (
          <Callout tone="danger" role="alert" className="sm:col-span-2">
            {failure}
          </Callout>
        ) : null}
        {meeting ? null : (
          <Select
            className="sm:col-span-2"
            label="Engagement"
            required
            placeholder={
              engagementsQuery.isLoading
                ? 'Loading your engagements…'
                : engagementOptions.length
                  ? 'Choose an engagement'
                  : 'No open engagements are assigned to you'
            }
            options={engagementOptions}
            value={form.engagement_id}
            error={errors.engagement_id}
            onChange={(e) => {
              set('engagement_id', e.target.value)
              set('objective_ids', [])
            }}
          />
        )}
        <Input
          className="sm:col-span-2"
          label="Title"
          required
          maxLength={MEETING_LIMITS.title.max}
          value={form.title}
          error={errors.title}
          onChange={(e) => set('title', e.target.value)}
        />
        <Select
          label="Meeting type"
          required
          options={MEETING_TYPES.values.map((v) => ({ value: v, label: v }))}
          value={form.meeting_type}
          onChange={(e) => set('meeting_type', e.target.value)}
        />
        <Select
          label="Time zone"
          options={zoneOptions(form.timezone)}
          value={form.timezone}
          error={errors.timezone}
          onChange={(e) => set('timezone', e.target.value)}
        />
        <Input
          type="datetime-local"
          label="Starts"
          required
          hint={zoneHint}
          value={form.starts}
          error={errors.starts_at}
          onChange={(e) => set('starts', e.target.value)}
        />
        <Input
          type="datetime-local"
          label="Ends"
          required
          value={form.ends}
          error={errors.ends_at}
          onChange={(e) => set('ends', e.target.value)}
        />
        <Input
          label="Format or location"
          required
          maxLength={MEETING_LIMITS.location.max}
          value={form.location}
          error={errors.location}
          onChange={(e) => set('location', e.target.value)}
        />
        <Input
          type="datetime-local"
          label="Participation deadline"
          hint="Optional. Defaults to the start time."
          value={form.cutoff}
          error={errors.participation_cutoff}
          onChange={(e) => set('cutoff', e.target.value)}
        />
        <Textarea
          className="sm:col-span-2"
          label="Agenda"
          required
          rows={4}
          maxLength={MEETING_LIMITS.agenda.max}
          value={form.agenda}
          error={errors.agenda}
          onChange={(e) => set('agenda', e.target.value)}
        />
        <Textarea
          className="sm:col-span-2"
          label="Company participants"
          hint="Optional. Names and roles, if known."
          rows={2}
          maxLength={MEETING_LIMITS.companyParticipants.max}
          value={form.company_participants}
          error={errors.company_participants}
          onChange={(e) => set('company_participants', e.target.value)}
        />
        <fieldset className="sm:col-span-2">
          <legend className="text-[13px] font-semibold text-teal-800">Related objectives</legend>
          <p className="mt-1 mb-2 text-xs text-sage">
            Up to {MEETING_LIMITS.objectives.max}, from this engagement only.
          </p>
          {objectiveOptions.length === 0 ? (
            <p className="text-sm text-sage">
              {engagementId
                ? 'This engagement has no objectives yet.'
                : 'Choose an engagement first.'}
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {objectiveOptions.map((o) => {
                const id = String(o.id)
                return (
                  <Checkbox
                    key={id}
                    label={String(o.title)}
                    checked={form.objective_ids.includes(id)}
                    onChange={(e) =>
                      set(
                        'objective_ids',
                        e.target.checked
                          ? [...form.objective_ids, id]
                          : form.objective_ids.filter((x) => x !== id),
                      )
                    }
                  />
                )
              })}
            </div>
          )}
          {errors.objective_ids ? (
            <p className="mt-1 text-[13px] text-danger">{errors.objective_ids}</p>
          ) : null}
        </fieldset>
      </form>
    </Dialog>
  )
}
